import { parse } from 'csv-parse/browser/esm/sync';
import type { Operation, RequestBody, ResponseBody } from '@platform/openapi';

export type ImportView = ResponseBody<Operation<'/admin/imports/{id}', 'get'>>;
export type ImportReport = NonNullable<ImportView['report']>;
export type ImportMapping = RequestBody<Operation<'/admin/imports', 'post'>>['mapping'];
export type MappedColumn = ImportMapping['columns'][number];
export type ColumnTarget = MappedColumn['target'];
export type ImportLocale = 'fr' | 'ar' | 'en';

export const IMPORT_LOCALES = ['fr', 'ar', 'en'] as const satisfies readonly ImportLocale[];

export const SCALAR_TARGETS = [
  'reference',
  'category',
  'sku',
  'barcode',
  'price',
  'regularPrice',
  'cost',
  'weightKg',
  'stock',
] as const satisfies readonly Extract<ColumnTarget, { field: string }>['field'][];

export type ScalarTarget = (typeof SCALAR_TARGETS)[number];

export const MAX_OPTION_COLUMNS = 3;
export const PREVIEW_ROWS = 5;
const PREVIEW_BYTES = 64 * 1024;

const DELIMITERS = [';', ',', '\t'] as const;
const QUOTE = '"';

export type Preview = { readonly headers: readonly string[]; readonly rows: readonly (readonly string[])[] };

export const delimiterOf = (header: string): string => {
  const outsideQuotes = header
    .split(QUOTE)
    .filter((_, position) => position % 2 === 0)
    .join('');
  const counts = DELIMITERS.map((delimiter) => ({
    delimiter,
    count: outsideQuotes.split(delimiter).length - 1,
  }));
  return counts.reduce((best, candidate) => (candidate.count > best.count ? candidate : best)).delimiter;
};

const completeLines = (text: string, whole: boolean): string =>
  whole ? text : text.slice(0, Math.max(0, text.lastIndexOf('\n')));

export const previewOf = (text: string, whole: boolean): Preview => {
  const content = completeLines(text.replace(/^﻿/u, ''), whole);
  const [firstLine = ''] = content.split(/\r?\n/u);
  const records: string[][] = parse(content, {
    delimiter: delimiterOf(firstLine),
    relax_column_count: true,
    relax_quotes: true,
    skip_empty_lines: true,
    to: PREVIEW_ROWS + 1,
  });
  const [headers = [], ...rows] = records;
  return { headers, rows };
};

export const readPreview = async (file: File): Promise<Preview> =>
  previewOf(await file.slice(0, PREVIEW_BYTES).text(), file.size <= PREVIEW_BYTES);

export const normalizedHeader = (header: string): string =>
  header
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

const SCALAR_SYNONYMS: Readonly<Record<ScalarTarget, readonly string[]>> = {
  reference: ['reference', 'ref', 'code produit', 'المرجع'],
  category: ['categorie', 'category', 'rayon', 'الفئة'],
  sku: ['sku', 'code article'],
  barcode: ['code barre', 'code barres', 'barcode', 'ean', 'gtin'],
  price: ['prix', 'price', 'prix de vente', 'sale price', 'السعر'],
  regularPrice: ['prix barre', 'ancien prix', 'regular price', 'compare at price'],
  cost: ['cout', 'cost', 'prix d achat', 'purchase price'],
  weightKg: ['poids', 'poids kg', 'weight', 'weight kg'],
  stock: ['stock', 'quantite', 'quantity', 'qty', 'الكمية'],
};

const TEXT_SYNONYMS = {
  name: ['nom', 'name', 'designation', 'produit', 'product', 'الاسم'],
  description: ['description', 'descriptif', 'الوصف'],
} as const;

const OPTION_SYNONYMS = [
  'couleur',
  'color',
  'colour',
  'taille',
  'size',
  'pointure',
  'genre',
  'gender',
  'matiere',
  'material',
];

const LOCALE_SUFFIX = /\s(fr|ar|en)$/u;

const textTarget = (header: string, defaultLocale: ImportLocale): ColumnTarget | null => {
  const suffix = LOCALE_SUFFIX.exec(header)?.[1];
  const base = suffix === undefined ? header : header.slice(0, -suffix.length - 1);
  const locale = IMPORT_LOCALES.find((candidate) => candidate === suffix) ?? defaultLocale;
  for (const field of ['name', 'description'] as const)
    if (TEXT_SYNONYMS[field].some((synonym) => synonym === base)) return { field, locale };
  return null;
};

export const suggestedTarget = (header: string, defaultLocale: ImportLocale): ColumnTarget | null => {
  const normalized = normalizedHeader(header);
  const scalar = SCALAR_TARGETS.find((field) => SCALAR_SYNONYMS[field].includes(normalized));
  if (scalar !== undefined) return { field: scalar };
  const text = textTarget(normalized, defaultLocale);
  if (text !== null) return text;
  return OPTION_SYNONYMS.includes(normalized) ? { field: 'option', name: header.trim() } : null;
};

export const targetKey = (target: ColumnTarget): string => {
  if (target.field === 'option') return `option:${normalizedHeader(target.name)}`;
  if (target.field === 'name' || target.field === 'description') return `${target.field}:${target.locale}`;
  return target.field;
};

export const suggestedMapping = (headers: readonly string[], defaultLocale: ImportLocale): MappedColumn[] => {
  const taken = new Set<string>();
  return headers.flatMap((header, index) => {
    const target = suggestedTarget(header, defaultLocale);
    if (target === null || taken.has(targetKey(target))) return [];
    taken.add(targetKey(target));
    return [{ index, header, target }];
  });
};

export type MappingProblem = 'reference' | 'price' | 'name' | 'duplicate' | 'options';

export const mappingProblems = (
  columns: readonly MappedColumn[],
  defaultLocale: ImportLocale,
): MappingProblem[] => {
  const keys = columns.map((column) => targetKey(column.target));
  return [
    ...(keys.includes('reference') ? [] : ['reference' as const]),
    ...(keys.includes('price') ? [] : ['price' as const]),
    ...(keys.includes(`name:${defaultLocale}`) ? [] : ['name' as const]),
    ...(new Set(keys).size === keys.length ? [] : ['duplicate' as const]),
    ...(keys.filter((key) => key.startsWith('option:')).length > MAX_OPTION_COLUMNS
      ? ['options' as const]
      : []),
  ];
};

export const maps = (columns: readonly MappedColumn[], field: ColumnTarget['field']): boolean =>
  columns.some((column) => column.target.field === field);

export const TEMPLATE_HEADERS = [
  'Référence',
  'Nom',
  'Description',
  'Catégorie',
  'Couleur',
  'Taille',
  'SKU',
  'Prix',
  'Prix barré',
  'Poids kg',
  'Stock',
];

export const TEMPLATE_ROWS = [
  ['SND-ETE', 'Sandale été', 'Sandale en cuir', 'Chaussures', 'Noir', '40', '', '79,000', '', '0,4', '5'],
  ['SND-ETE', 'Sandale été', 'Sandale en cuir', 'Chaussures', 'Noir', '41', '', '79,000', '', '0,4', '3'],
];

const quoted = (cell: string): string => (/[;"\n]/u.test(cell) ? `"${cell.replaceAll('"', '""')}"` : cell);

export const templateCsv = (): string =>
  `﻿${[TEMPLATE_HEADERS, ...TEMPLATE_ROWS].map((row) => row.map(quoted).join(';')).join('\r\n')}\r\n`;
